import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Badge, Button, Form, InputGroup, Modal, Spinner, Table } from "react-bootstrap";
import { AiOutlineSearch } from "react-icons/ai";
import axios from "axios";
import WeekSelector from "../../weekSelector";

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

const STATUS_FILTERS = ["all", "rejected", "pending", "confirmed", "processed"];

const FleetVehicleUsageLogs = () => {
  const { t } = useTranslation();
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [items, setItems] = useState([]);
  const [weekRange, setWeekRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [processModal, setProcessModal] = useState(null);
  const [adminComment, setAdminComment] = useState("");
  const [processing, setProcessing] = useState(false);

  const load = (week) => {
    if (!week) return;
    setLoading(true);
    setError("");
    axios
      .get("/api/vehicle-usage-logs", { params: { week }, ...authHeaders() })
      .then((res) => {
        setItems(res.data?.items || []);
        setWeekRange(res.data?.weekRange || null);
      })
      .catch((err) => setError(err.response?.data?.error || t("fleet.loadError")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (selectedWeek != null) load(selectedWeek);
  }, [selectedWeek]);

  const filtered = useMemo(() => {
    let list = items;
    if (statusFilter !== "all") {
      list = list.filter((i) => i.status === statusFilter);
    }
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    const qCompact = q.replace(/\s+/g, "");
    return list.filter((i) => {
      const plate = (i.plate || "").toLowerCase();
      const driver = (i.driverName || "").toLowerCase();
      const plateCompact = plate.replace(/\s+/g, "");
      return plate.includes(q) || driver.includes(q) || (qCompact && plateCompact.includes(qCompact));
    });
  }, [items, searchQuery, statusFilter]);

  const rowClass = (status) => {
    if (status === "confirmed") return "table-success";
    if (status === "rejected") return "table-danger";
    if (status === "pending") return "table-warning";
    return "";
  };

  const statusBadge = (status) => {
    const map = {
      confirmed: "success",
      rejected: "danger",
      pending: "warning",
      processed: "secondary"
    };
    const labels = {
      confirmed: t("fleet.usageLogStatusConfirmed"),
      rejected: t("fleet.usageLogStatusRejected"),
      pending: t("fleet.usageLogStatusPending"),
      processed: t("fleet.usageLogStatusProcessed")
    };
    return <Badge bg={map[status] || "light"}>{labels[status] || status}</Badge>;
  };

  const openProcess = (item) => {
    setProcessModal(item);
    setAdminComment(item.admin_comment || "");
  };

  const submitProcess = async () => {
    if (!processModal?.id) return;
    setProcessing(true);
    try {
      await axios.patch(
        `/api/vehicle-usage-logs/${processModal.id}/process`,
        { admin_comment: adminComment },
        authHeaders()
      );
      setProcessModal(null);
      load(selectedWeek);
    } catch (err) {
      setError(err.response?.data?.error || t("fleet.usageLogProcessError"));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="container-fluid mt-4 px-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <h2 className="mb-0">{t("fleet.usageLogPageTitle")}</h2>
        <WeekSelector selectedWeek={selectedWeek} setSelectedWeek={setSelectedWeek} />
      </div>

      {weekRange && (
        <p className="text-muted small">
          {t("fleet.usageLogWeekRange", {
            from: weekRange.monday,
            to: weekRange.sunday
          })}
        </p>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
        <InputGroup style={{ maxWidth: 360 }}>
          <InputGroup.Text>
            <AiOutlineSearch />
          </InputGroup.Text>
          <Form.Control
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("fleet.searchPlaceholder")}
          />
        </InputGroup>
        <Form.Select
          style={{ maxWidth: 220 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {s === "all"
                ? t("fleet.usageLogFilterAll")
                : t(`fleet.usageLogStatus${s.charAt(0).toUpperCase()}${s.slice(1)}`)}
            </option>
          ))}
        </Form.Select>
        {loading && <Spinner animation="border" size="sm" />}
      </div>

      <Table bordered hover responsive>
        <thead>
          <tr>
            <th>{t("fleet.driver")}</th>
            <th>{t("fleet.vehicle")}</th>
            <th>{t("fleet.usageLogWeek")}</th>
            <th>{t("fleet.usageLogStatus")}</th>
            <th>{t("fleet.usageLogResponse")}</th>
            <th>{t("fleet.usageLogAdminComment")}</th>
            <th>{t("fleet.ownerHistoryActions")}</th>
          </tr>
        </thead>
        <tbody>
          {!loading && filtered.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center text-muted">
                {t("fleet.usageLogEmpty")}
              </td>
            </tr>
          )}
          {filtered.map((item) => (
            <tr
              key={item.id || `p-${item.driver_id}-${item.vehicle_id}`}
              className={rowClass(item.status)}
            >
              <td>{item.driverName || `#${item.driver_id}`}</td>
              <td>
                {[item.plate, [item.brand, item.model].filter(Boolean).join(" ")]
                  .filter(Boolean)
                  .join(" — ")}
              </td>
              <td>{item.week_number || selectedWeek}</td>
              <td>{statusBadge(item.status)}</td>
              <td className="small">{item.driver_response || "—"}</td>
              <td className="small">{item.admin_comment || "—"}</td>
              <td>
                {item.status === "rejected" && item.id ? (
                  <Button size="sm" variant="outline-dark" onClick={() => openProcess(item)}>
                    {t("fleet.usageLogMarkProcessed")}
                  </Button>
                ) : (
                  "—"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={!!processModal} onHide={() => !processing && setProcessModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("fleet.usageLogMarkProcessed")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-2">
            {processModal?.driverName} — {processModal?.plate}
          </p>
          <Form.Group>
            <Form.Label>{t("fleet.usageLogAdminComment")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={adminComment}
              onChange={(e) => setAdminComment(e.target.value)}
              disabled={processing}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setProcessModal(null)} disabled={processing}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={submitProcess} disabled={processing}>
            {processing ? t("fleet.saving") : t("fleet.usageLogMarkProcessed")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FleetVehicleUsageLogs;
